import { ReportDefinition, ReportHeaderSnippet } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function asNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   const amount = Number(value);
   return Number.isFinite(amount) ? amount : null;
}

function formatCurrency(value: any): string {
   const amount = asNumberOrNull(value);
   if (amount === null) return '';

   const formatted = Math.abs(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return amount < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatDate(value: any): string {
   const raw = asString(value).trim();

   if (!raw) return '';

   const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

   if (!match) {
      return raw;
   }

   return `${match[3]}/${match[2]}/${match[1]}`;
}

function getComplimentaryRoomRows(reportJson: any): any[] {
   const parsedReportJson = parseReportJson(reportJson);

   return Array.isArray(parsedReportJson?.ComplimentaryRooms)
      ? parsedReportJson.ComplimentaryRooms
      : [];
}

function getRowId(row: any): string {
   return (
      asString(row?.TyallsRowId) ||
      asString(row?.tyallsRowId) ||
      `comp:${asString(row?.ReservationId)}:${asString(
         row?.ConfirmationNumber
      )}:${asString(row?.RoomNumber)}`
   );
}

function getGuestNameWithComments(row: any): string {
   const name = asString(row?.Name);
   const comments = asString(row?.Comments);

   if (!comments.trim()) {
      return name;
   }

   return `${name}\n${comments}`;
}

function getCompany(row: any): string {
   return asString(row?.CompCompanyAgentGroup) || asString(row?.RoutedTo) || '-';
}

function getBalance(row: any): number | null {
   return asNumberOrNull(row?.Balance);
}

function flattenRows(reportJson: any): ReportRowVm[] {
   return [...getComplimentaryRoomRows(reportJson)]
      .sort((a, b) => {
         return (
            asString(a?.RoomNumber).localeCompare(asString(b?.RoomNumber)) ||
            asString(a?.Name).localeCompare(asString(b?.Name))
         );
      })
      .map((row) => {
         const balance = getBalance(row);

         return {
            rowId: getRowId(row),
            cells: {
               rowType: 'complimentaryRoom',

               room: asString(row?.RoomNumber),
               guestName: getGuestNameWithComments(row),
               guestNameRaw: asString(row?.Name),
               comments: asString(row?.Comments),

               arrivalDate: formatDate(row?.ArrivalDate),
               arrivalDateRaw: asString(row?.ArrivalDate),

               departureDate: formatDate(row?.DepartureDate),
               departureDateRaw: asString(row?.DepartureDate),

               company: getCompany(row),
               balance: formatCurrency(balance),
               balanceRaw: balance,

               rateCode: asString(row?.RateCode),

               roomType: asString(row?.RoomType),
               persons: asString(row?.Persons),
               paymentMethod: asString(row?.PaymentMethod),
               marketCode: asString(row?.MarketCode),
               blockCode: asString(row?.BlockCode)
            }
         };
      });
}

function getHeaderSnippets(reportJson: any): ReportHeaderSnippet[] {
   const rows = getComplimentaryRoomRows(reportJson);

   const totalBalance = rows.reduce((total, row) => {
      const balance = getBalance(row);
      return balance !== null ? total + balance : total;
   }, 0);

   return [
      {
         label: 'Count',
         value: rows.length,
         tone: rows.length > 0 ? 'info' : 'success'
      },
      {
         label: 'Total Balance',
         value: formatCurrency(totalBalance),
         tone: totalBalance < 0 ? 'danger' : 'success'
      }
   ];
}

export const COMPLIMENTARY_ROOMS_DEFINITION: ReportDefinition = {
   reportType: 'ComplimentaryRooms',
   version: 2,
   title: 'Complimentary Rooms',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Complimentary Rooms',
      snippets: ({ reportData }) => getHeaderSnippets(reportData)
   },

   rowChecks: {
      enabled: false
   },

   annotations: {
      enabled: true
   },

   columns: [
      {
         key: 'room',
         header: 'ROOM',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '280px',
         maxChars: 120,
         sort: {
            type: 'string',
            getValue: (row) => row.cells['guestNameRaw']
         }
      },
      {
         key: 'arrivalDate',
         header: 'ARR. DATE',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['arrivalDateRaw']
         }
      },
      {
         key: 'departureDate',
         header: 'DEP. DATE',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['departureDateRaw']
         }
      },
      {
         key: 'company',
         header: 'COMPANY',
         width: '260px',
         maxChars: 90,
         sort: { type: 'string' }
      },
      {
         key: 'balance',
         header: 'BALANCE',
         width: '120px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['balanceRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'rateCode',
         header: 'RATE CODE',
         width: '120px',
         sort: { type: 'string' }
      }
   ],

   flattenRows,

   filter: {
      fields: [
         { key: 'room', label: 'Room', type: 'string' },
         {
            key: 'guestName',
            label: 'Guest Name',
            type: 'string',
            getValue: (row) => row.cells['guestNameRaw']
         },
         { key: 'comments', label: 'Comments', type: 'string' },
         { key: 'arrivalDate', label: 'Arrival Date', type: 'date' },
         { key: 'departureDate', label: 'Departure Date', type: 'date' },
         { key: 'company', label: 'Company', type: 'string' },
         { key: 'rateCode', label: 'Rate Code', type: 'string' },
         {
            key: 'balance',
            label: 'Balance',
            type: 'number',
            getValue: (row) => row.cells['balanceRaw']
         }
      ]
   }
};
